import { PageHero } from '@components/common';
import { ImageCardGrid } from '@components/sections';
import { Building2, ArrowRight } from 'lucide-react';
import { CALENDLY_URL } from '@constants';
import { sectorCards } from '@data/sectorCards';

export default function SectorsPage() {
  // ItemList structured data for SEO
  const sectorsStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: sectorCards.map((sector, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'WebPage',
        name: sector.title,
        description: sector.description,
        url: `https://thecisonetwork.com${sector.path}`,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(sectorsStructuredData) }}
      />
      <PageHero
        title="Industry-Specific Cybersecurity Expertise"
        subtitle="Fractional CISO services tailored to the unique security challenges and regulatory requirements of your sector"
        eyebrow="Sectors"
        seoTitle="Fractional CISO by Industry | The CISO Network"
        seoDescription="Expert fractional CISO services for SaaS, automotive, energy, financial services, healthcare, legal, manufacturing, retail and private equity. Industry-specific security leadership."
      />

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              Security Leadership That Understands Your Industry
            </h2>
            <p className="text-lg text-muted-foreground">
              Every sector faces unique cybersecurity challenges. From FCA regulations in financial
              services to patient data protection in healthcare, our fractional CISOs bring deep
              industry expertise to address your specific risks.
            </p>
          </div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <ImageCardGrid cards={sectorCards} />
        </div>
      </section>

      {/* Why Industry-Specific */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Where Industry Expertise Matters
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      Regulatory Knowledge
                    </h3>
                    <p className="text-muted-foreground">
                      Each industry has its own compliance landscape. Our sector specialists
                      understand the frameworks that matter to you.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      Threat Awareness
                    </h3>
                    <p className="text-muted-foreground">
                      Different sectors attract different attackers. We know the threat actors and
                      techniques targeting your industry.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      Business Context
                    </h3>
                    <p className="text-muted-foreground">
                      Security must enable business, not block it. Our CISOs understand how your
                      industry works and what matters most.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  Need a Different Sector?
                </h3>
              </div>
              <p className="text-muted-foreground mb-6 text-center">
                While these are key focus areas, our CISOs have experience across many industries.
                Contact us to discuss your specific sector requirements.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero w-full justify-center"
              >
                Discuss Your Sector
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Find Your Industry Specialist
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call and we will match you with a fractional CISO who understands your
            sector's unique challenges.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
